import React, { useContext, useReducer, FormEvent, useEffect, useState, forwardRef, useImperativeHandle, FC, useRef, ReactNode, Reducer } from 'react';
import { type AnyAction } from 'typescript-fsa';
import { INITIAL_STATE, formContext } from './hooks';
import { reducer, setField, setSubmitting, setGlobalErrors, clearField } from './state';
import { getValues, getErrors, hasErrors, getFirstErrorName, valuesToFields } from './utils';
import { FormState, FormValues, FormField, FormFields, SubmitErrorList } from './@types';
import type { CollateErrorsProps, AddressFormErrors, Messages } from '@ynap/address-form-v1';

export type FormChildrenFunction = (submitting: boolean, errors: Array<string>, setTouched: unknown) => ReactNode;

type AnyFn = (...args: unknown[]) => unknown;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFunction = (f: any): f is AnyFn => {
    return f instanceof Function;
};

export interface FormProps {
    className?: string;
    messages: Messages;
    name: string;
    children?: FormChildrenFunction | ReactNode;
    initialValues?: FormValues;
    onSubmit?: (values: FormValues) => Promise<unknown>;
    reduxForm?: {
        name: string;
        blur: AnyFn;
        change: AnyFn;
    };
    touched?: boolean;
    collateErrors: (values: FormValues, props: CollateErrorsProps, errors: SubmitErrorList) => AddressFormErrors;
}

export interface WrappedProps {
    formRef: React.Ref<typeof Form>;
    addressFormTouched: boolean;
    setAddressFormTouched: (touched: boolean) => void;
}

export type FormRefObject = {
    hasErrors: () => boolean;
    getErrors: () => FormValues;
    getValues: () => FormValues;
    setField: (payload: { name: string; field: Partial<FormField> | FormFields }) => void;
    setTouched: (touched: boolean) => void;
    scrollToFirstError: () => void;
};

/**
 * React Local Form - a set of form helpers using context and hooks.
 */
const Form: React.RefForwardingComponent<FormRefObject, FormProps> = (
    { className, name: formName, initialValues, onSubmit, children, reduxForm, touched, messages, collateErrors },
    ref,
) => {
    const { name: parentName, touched: parentTouched, dispatch: parentDispatch } = useContext(formContext);
    const root = typeof onSubmit != 'undefined';
    const name = parentName ? `${parentName}.${formName}` : formName;
    const [myTouched, setTouched] = useState(false);
    const initialState: FormState = { ...INITIAL_STATE, fields: initialValues ? valuesToFields(initialValues) : INITIAL_STATE.fields };
    const [state, dispatch] = useReducer<Reducer<FormState, AnyAction>>(reducer(initialState), initialState);
    const formTouched = touched || parentTouched || myTouched;

    useEffect(() => {
        // child of another Form, pass state objects upwards
        if (!root && typeof parentDispatch == 'function') {
            parentDispatch(setField({ name: formName, field: state.fields }));
        }
        // redux-form mode
        if (reduxForm) {
            const values = getValues(state.fields);
            reduxForm.blur(formName, values);
        }
    }, [state]);

    // Unmount
    useEffect(() => {
        return () => {
            if (!root && typeof parentDispatch == 'function') {
                parentDispatch(clearField(formName));
            }
            if (reduxForm) {
                // reduxForm.blur(formName, {})
            }
        };
    }, []);

    // Used to add methods to the ref, so that wrapping parent components can access the form's validation state.
    // TODO: remove this when we refactor PaymentStep to remove redux-form
    useImperativeHandle(
        ref,
        () => ({
            hasErrors: () => hasErrors(state.fields) || state.globalErrors.length > 0,
            getErrors: () => getErrors(state.fields),
            getValues: () => getValues(state.fields),
            setField: (payload: { name: string; field: Partial<FormField> | FormFields }) => dispatch(setField(payload)),
            setTouched: (value: boolean) => setTouched(value),
            scrollToFirstError,
        }),
        [state, myTouched, setTouched],
    );

    async function getSubmitError(error: any, values: FormValues) {
        if (typeof error?.response == 'string') return {};

        if (!error?.response?.bodyUsed) {
            try {
                return await error.response.json().then((errorJson: SubmitErrorList) => collateErrors(values, { messages }, errorJson));
            } catch (e) {
                return { _error: messages?.errors?.generic?.() };
            }
        }
        return collateErrors(values, { messages }, error);
    }
    function scrollToFirstError() {
        const fieldWithErrorId = getFirstErrorName(state.fields);
        if (fieldWithErrorId) {
            const errorField = document.getElementById(fieldWithErrorId);
            errorField?.parentElement?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }
    }
    async function handleSubmit(ev?: FormEvent<HTMLFormElement>) {
        ev?.preventDefault();
        dispatch(setSubmitting(true));
        if (root) {
            setTouched(true);
        }

        if (!hasErrors(state.fields) && onSubmit) {
            const values = getValues(state.fields);
            try {
                await onSubmit(values);
            } catch (error) {
                const errorObj = await getSubmitError(error, values);
                Object.keys(errorObj).forEach((name) => {
                    const error = errorObj[name];
                    if (name === '_error') {
                        dispatch(setGlobalErrors([errorObj._error]));
                    } else {
                        dispatch(setField({ name, field: { errors: [error] } }));
                    }
                });
            }
            dispatch(setSubmitting(false));
        } else {
            dispatch(setSubmitting(false));
            scrollToFirstError();
        }
    }
    const renderedChildren = isFunction(children) ? children(state.submitting, state.globalErrors, setTouched) : children;

    return (
        <formContext.Provider
            value={{
                name: root ? undefined : name,
                root: false,
                state: state as FormState,
                dispatch,
                touched: formTouched,
            }}
        >
            {root ? (
                <form id={name} className={className} onSubmit={handleSubmit}>
                    {renderedChildren}
                </form>
            ) : (
                <div id={name} className={className}>
                    {renderedChildren}
                </div>
            )}
        </formContext.Provider>
    );
};

interface WithFormRef {
    (Component: FC<FormProps & WrappedProps>): FC<FormProps>;
}

/**
 * A higher-order component that injects a `Form` ref into the component's props.
 *
 * @param Component
 */
export const withFormRef: WithFormRef = (Component: React.FC<FormProps & WrappedProps>) => {
    // eslint-disable-next-line react/display-name
    return (props: FormProps) => {
        const formRef = useRef<typeof Form>(null);
        const [addressFormTouched, setAddressFormTouched] = useState(false);

        return (
            <Component
                formRef={formRef}
                addressFormTouched={addressFormTouched}
                setAddressFormTouched={(newValue) => {
                    return setAddressFormTouched(newValue);
                }}
                {...props}
            />
        );
    };
};

export default forwardRef(Form);
